import { Component, computed, forwardRef, HostBinding, inject, Input, signal, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SectorDropdownService } from './sector-dropdown.service';
import { FloatLabel } from 'primeng/floatlabel';
import {
  BsAutocompleteComponent,
  BsFloatLabelCompatDirective
} from 'iriro-frontend-libs/bs-component-store';

@Component({
  selector: 'well-sector-dropdown',
  host: { class: 'irian-custom-cva' },
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FloatLabel,
    BsAutocompleteComponent,
    BsFloatLabelCompatDirective
  ],
  templateUrl: './sector-dropdown.component.html',
  styleUrl: './sector-dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SectorDropdownComponent),
      multi: true,
    }
  ],
})
export class SectorDropdownComponent implements ControlValueAccessor {
  get selectedSector(): string | null {
    return this._selectedSector;
  }

  set selectedSector(value: string | null) {
    this.filled = !!value;
    this._selectedSector = value;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChangeCallback = (value: any) => {
    // No-op default implementation
  };
  onTouchedCallback: any | undefined = undefined;
  disabled = false;
  @Input() label: string | undefined = undefined;
  @Input() floatLabelVariant: 'in' | 'over' | 'on' = 'on';

  private _selectedSector: string | null = null;

  sectors = inject(SectorDropdownService).getSectors();

  private query: WritableSignal<any> = signal({ value: '' });

  @HostBinding('class.p-inputwrapper-focus') focus = false;
  @HostBinding('class.p-inputwrapper-filled') filled = false;

  options = computed(() => {
    return this.sectors.filter((item) => item?.toLowerCase().includes(this.query().value.toLowerCase()));
  });

  onQueryChange(query: string) {
    this.query.set({ value: query, data: Date.now() });
  }

  onChange() {
    if (this.onChangeCallback) {
      if (this.selectedSector && this.sectors.includes(this.selectedSector)) {
        this.onChangeCallback(this.selectedSector);
      } else {
        this.selectedSector = null;
        this.onChangeCallback(this.selectedSector);
      }
    }
  }

  onClear() {
    this.selectedSector = null;
    this.onChange();
  }

  onSelect() {
    this.onChange();
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(sector: string | null): void {
    if (sector != this.selectedSector) {
      if (!sector) {
        this.selectedSector = null;
      }
      if (sector) {
        this.selectedSector = sector;
      }
      this.onChange();
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  onBlur() {
    this.focus = false;
    this.onChange();
    if (this.onTouchedCallback) {
      this.onTouchedCallback(this.selectedSector);
    }
  }

  onInputFocus() {
    this.focus = true;
    if (this.onTouchedCallback) {
      this.onTouchedCallback(this.selectedSector);
    }
  }
}
